/*
  Header menu product grids, loaded on demand.

  as-header.liquid prints an empty placeholder (carrying data-lazy-src = the collection URL) for every
  product grid in the desktop mega menu and the mobile drawer. When a menu is opened, this script asks
  Shopify's Section Rendering API for sections/as-header-products.liquid in the context of that
  collection and swaps the cards in. Each collection is fetched at most once per page view.

  Desktop: hovering or focusing a top-level menu item loads every grid inside it, so the cards are
  usually in place before the pointer reaches a sub-collection.
  Mobile: opening a level-2 menu loads all of its level-3 grids; opening a level-3 panel loads that one.
*/
(() => {
  const SECTION_ID = 'as-header-products';
  const requests = new Map();

  function fetchGrid(src) {
    if (!requests.has(src)) {
      const url = `${src}${src.includes('?') ? '&' : '?'}section_id=${SECTION_ID}`;
      const request = fetch(url, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        })
        .then((html) => new DOMParser().parseFromString(html, 'text/html').querySelector('[data-header-products]'))
        .catch((error) => {
          requests.delete(src);
          throw error;
        });
      requests.set(src, request);
    }
    return requests.get(src);
  }

  // as-header.js sizes the custom scrollbars on window resize. Several grids usually land together,
  // so one resize per animation frame instead of one per grid (every resize handler on the page runs).
  let resizeQueued = false;
  function requestScrollbarUpdate() {
    if (resizeQueued) return;
    resizeQueued = true;
    requestAnimationFrame(() => {
      resizeQueued = false;
      window.dispatchEvent(new Event('resize'));
    });
  }

  function fallbackLink(src) {
    const link = document.createElement('a');
    link.className = 'as-header-view-all';
    link.href = src;
    link.textContent = 'View all products';
    return link;
  }

  async function fill(target) {
    if (!target || target.dataset.lazyState) return;
    const src = target.dataset.lazySrc;
    if (!src) return;
    target.dataset.lazyState = 'loading';

    try {
      const grid = await fetchGrid(src);
      const fragment = grid ? document.importNode(grid, true) : null;

      if (target.classList.contains('as-mob-products')) {
        // The drawer lists every card in its own 2-column grid, without the desktop's "show 6" limit.
        const items = fragment ? [...fragment.querySelectorAll('.submenu-products > *')] : [];
        items.forEach((item) => item.classList.remove('is-hidden'));
        target.replaceChildren(...items);
      } else {
        target.replaceChildren(...(fragment ? fragment.children : []));
      }

      target.dataset.lazyState = 'done';
      requestScrollbarUpdate();
    } catch (error) {
      target.replaceChildren(fallbackLink(src));
      target.dataset.lazyState = 'error';
    }
  }

  function fillAll(scope, selector) {
    scope.querySelectorAll(selector).forEach(fill);
  }

  function init() {
    document.querySelectorAll('.as-header__details').forEach((details) => {
      const load = () => fillAll(details, '.submenu-products-wrap[data-lazy-src]');
      details.addEventListener('mouseenter', load, { once: true });
      details.addEventListener('focusin', load, { once: true });
    });

    const drawer = document.querySelector('.as-mob-drawer');
    if (!drawer) return;

    drawer.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-next-level]');
      if (!trigger) return;
      const menuId = trigger.dataset.menuId || '';

      if (trigger.dataset.nextLevel === '2' && menuId.startsWith('menu-')) {
        const prefix = `submenu-${menuId.slice('menu-'.length)}-`;
        drawer.querySelectorAll('.as-mob-panel[data-menu-id]').forEach((panel) => {
          if (panel.dataset.menuId.startsWith(prefix)) fillAll(panel, '[data-lazy-src]');
        });
      } else {
        const panel = drawer.querySelector(`.as-mob-panel[data-menu-id="${CSS.escape(menuId)}"]`);
        if (panel) fillAll(panel, '[data-lazy-src]');
      }
    });
  }

  document.addEventListener('DOMContentLoaded', init);

  // Theme editor: the header section is re-rendered in place, so bind the new elements.
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('.as-header-root')) init();
  });
})();
